import { ArrowLeft, Banknote, Building2, CalendarDays, Globe, Mail, MapPin, PenSquare, Pencil, Phone, Signal, Tag, User, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ICompany, CompanyStatus, CompanySize, FundingStage } from "../../../interfaces/company";
import { getAdminCompany } from "../../../interfaces/admin/companies";
import { createSignal } from "../../../interfaces/signals";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminCompanyDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [company, setCompany] = useState<ICompany | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [detectedSignals, setDetectedSignals] = useState<any[]>([]);
    const [creatingSignal, setCreatingSignal] = useState<string | null>(null);
    const [editingSignal, setEditingSignal] = useState<string | null>(null);
    const [publishImmediately, setPublishImmediately] = useState(false);

    useEffect(() => {
        if (id) {
            fetchCompany(id);
        }
    }, [id]);

    useEffect(() => {
        if (company) {
            analyzeCompanyForSignals(company);
        }
    }, [company]);

    const analyzeCompanyForSignals = (companyData: ICompany) => {
        const signals: any[] = [];
        const now = new Date();
        const companyCreatedAt = companyData.createdAt ? new Date(companyData.createdAt) : null;

        // Helper function to get primary contact info
        const getPrimaryContact = () => {
            if (companyData.founders && companyData.founders.length > 0) {
                return companyData.founders[0];
            }
            if (companyData.contacts && companyData.contacts.length > 0) {
                return companyData.contacts.find(c => c.isPrimary) || companyData.contacts[0];
            }
            return null;
        };

        const primaryContact = getPrimaryContact();
        const contactInfo = primaryContact ? {
            name: primaryContact.name || `${primaryContact.firstName || ''} ${primaryContact.lastName || ''}`.trim(),
            email: primaryContact.email,
            title: primaryContact.title
        } : null;

        // 1. Young company signals (created less than 3 years ago)
        if (companyCreatedAt) {
            const yearsSinceCreation = (now.getTime() - companyCreatedAt.getTime()) / (1000 * 60 * 60 * 24 * 365);
            
            if (yearsSinceCreation < 1) {
                signals.push({
                    id: 'young-company-1year',
                    type: 'expansion',
                    title: `${companyData.name} - Startup récente en croissance`,
                    description: `${companyData.name} a été créée il y a ${Math.round(yearsSinceCreation * 12)} mois. Phase critique de développement avec besoins en structuration juridique et comptable.${contactInfo ? ` Contact principal: ${contactInfo.name}${contactInfo.title ? ` (${contactInfo.title})` : ''}` : ''}`,
                    importance: 'high',
                    reason: `Entreprise créée il y a ${Math.round(yearsSinceCreation * 12)} mois`,
                    actionSuggestion: 'Services juridiques startup: statuts, BSPCE, contrats commerciaux, protection IP',
                    estimatedValue: '15000-50000',
                    urgency: 'high',
                    contactInfo
                });
            } else if (yearsSinceCreation < 3) {
                signals.push({
                    id: 'young-company-3years',
                    type: 'expansion',
                    title: `${companyData.name} - Scale-up en développement`,
                    description: `${companyData.name} a ${Math.round(yearsSinceCreation)} ans. Phase de scale-up nécessitant optimisation et structuration avancée.${contactInfo ? ` Contact: ${contactInfo.name}` : ''}`,
                    importance: 'medium',
                    reason: `Entreprise de ${Math.round(yearsSinceCreation)} ans en phase de croissance`,
                    actionSuggestion: 'Structuration avancée, optimisation fiscale, conformité RGPD, contrats partenaires',
                    estimatedValue: '25000-75000',
                    urgency: 'medium',
                    contactInfo
                });
            }
        }

        // 2. Enhanced founder analysis
        if (companyData.founders && companyData.founders.length > 0) {
            companyData.founders.forEach((founder, index) => {
                if (founder.title && (
                    founder.title.toLowerCase().includes('président') ||
                    founder.title.toLowerCase().includes('fondateur') ||
                    founder.title.toLowerCase().includes('ceo') ||
                    founder.title.toLowerCase().includes('directeur général')
                )) {
                    signals.push({
                        id: `founder-succession-${index}`,
                        type: 'executive_change',
                        title: `${companyData.name} - Potentiel de transmission`,
                        description: `${founder.name || `${founder.firstName} ${founder.lastName}`} est ${founder.title} de ${companyData.name}. Opportunité d'accompagnement en transmission ou cession.${founder.email ? ` Email: ${founder.email}` : ''}`,
                        importance: 'medium',
                        reason: `Dirigeant senior identifié: ${founder.title}`,
                        actionSuggestion: 'Due diligence, valorisation d\'entreprise, accompagnement transmission, audit legal',
                        estimatedValue: '50000-200000',
                        urgency: 'low',
                        contactInfo: {
                            name: founder.name || `${founder.firstName} ${founder.lastName}`,
                            email: founder.email,
                            title: founder.title
                        }
                    });
                }
            });

            // Multiple founders signal
            if (companyData.founders.length > 1) {
                signals.push({
                    id: 'multiple-founders',
                    type: 'other',
                    title: `${companyData.name} - Gouvernance multi-fondateurs`,
                    description: `${companyData.name} compte ${companyData.founders.length} fondateurs. Risques de conflits associés et besoins en gouvernance.`,
                    importance: 'low',
                    reason: `${companyData.founders.length} fondateurs détectés`,
                    actionSuggestion: 'Pacte d\'associés, gouvernance, médiation, protection des intérêts',
                    estimatedValue: '10000-40000',
                    urgency: 'low',
                    contactInfo
                });
            }
        }

        // 3. Enhanced company size milestones
        if (companyData.size) {
            const sizeMap: { [key: string]: { threshold: string, importance: any, value: string } } = {
                '11-50': { 
                    threshold: '15 salariés', 
                    importance: 'high', 
                    value: '20000-60000' 
                },
                '51-200': { 
                    threshold: '50 salariés', 
                    importance: 'medium', 
                    value: '30000-100000' 
                },
                '201-500': { 
                    threshold: '200 salariés', 
                    importance: 'medium', 
                    value: '50000-150000' 
                },
                '500+': { 
                    threshold: '500 salariés', 
                    importance: 'low', 
                    value: '75000-300000' 
                }
            };

            if (sizeMap[companyData.size]) {
                const sizeInfo = sizeMap[companyData.size];
                signals.push({
                    id: `growth-milestone-${companyData.size}`,
                    type: 'expansion',
                    title: `${companyData.name} - Seuil ${sizeInfo.threshold} franchi`,
                    description: `${companyData.name} emploie ${companyData.size} personnes. Nouvelles obligations légales et opportunités d'optimisation.${contactInfo ? ` Contact RH/Direction: ${contactInfo.name}` : ''}`,
                    importance: sizeInfo.importance,
                    reason: `Entreprise de ${companyData.size} employés`,
                    actionSuggestion: 'Droit social, conformité URSSAF, CSE, accords d\'entreprise, optimisation RH',
                    estimatedValue: sizeInfo.value,
                    urgency: 'medium',
                    contactInfo
                });
            }
        }

        // 4. Enhanced funding stage opportunities
        if (companyData.fundingStage) {
            const fundingMap: { [key: string]: { amount: number, importance: any, value: string } } = {
                'seed': { amount: 500000, importance: 'high', value: '15000-50000' },
                'series_a': { amount: 3000000, importance: 'critical', value: '50000-150000' },
                'series_b': { amount: 10000000, importance: 'critical', value: '100000-300000' },
                'series_c': { amount: 25000000, importance: 'critical', value: '200000-500000' }
            };

            if (fundingMap[companyData.fundingStage]) {
                const funding = fundingMap[companyData.fundingStage];
                signals.push({
                    id: `funding-${companyData.fundingStage}`,
                    type: companyData.fundingStage as any,
                    title: `${companyData.name} - Levée ${companyData.fundingStage.toUpperCase()}`,
                    description: `${companyData.name} est en phase ${companyData.fundingStage}. Opportunité high-value pour services juridiques et fiscaux spécialisés.${contactInfo ? ` CFO/CEO: ${contactInfo.name}` : ''}`,
                    importance: funding.importance,
                    reason: `Financement ${companyData.fundingStage} (≈${funding.amount.toLocaleString()}€)`,
                    actionSuggestion: 'Due diligence, structuration juridique, optimisation fiscale, data room',
                    estimatedValue: funding.value,
                    urgency: 'high',
                    contactInfo
                });
            }
        }

        // 5. Industry-specific enhanced signals
        if (companyData.industry) {
            const industrySignals: { [key: string]: any } = {
                'technology': {
                    title: 'Entreprise Tech - Besoins spécialisés',
                    action: 'Propriété intellectuelle, RGPD, contrats SaaS, cybersécurité',
                    value: '20000-80000'
                },
                'fintech': {
                    title: 'FinTech - Réglementation bancaire',
                    action: 'Conformité ACPR, PSD2, AML, licence bancaire, RGPD renforcé',
                    value: '50000-200000'
                },
                'healthtech': {
                    title: 'HealthTech - Réglementation santé',
                    action: 'Conformité CNIL santé, certification CE médical, RGPD santé',
                    value: '30000-120000'
                },
                'ecommerce': {
                    title: 'E-commerce - Conformité digitale',
                    action: 'CGV e-commerce, RGPD, droit consommation, conformité plateformes',
                    value: '15000-60000'
                }
            };

            for (const [key, config] of Object.entries(industrySignals)) {
                if (companyData.industry.toLowerCase().includes(key)) {
                    signals.push({
                        id: `industry-${key}`,
                        type: 'other',
                        title: `${companyData.name} - ${config.title}`,
                        description: `${companyData.name} évolue dans ${companyData.industry}. Secteur réglementé avec besoins juridiques spécifiques.`,
                        importance: 'medium',
                        reason: `Secteur spécialisé: ${companyData.industry}`,
                        actionSuggestion: config.action,
                        estimatedValue: config.value,
                        urgency: 'medium',
                        contactInfo
                    });
                    break;
                }
            }
        }

        // 6. Enhanced last contact analysis
        if (companyData.lastContactAt) {
            const lastContact = new Date(companyData.lastContactAt);
            const daysSinceContact = (now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24);
            
            if (daysSinceContact > 180) {
                signals.push({
                    id: 'reconnect',
                    type: 'other',
                    title: `${companyData.name} - Relance client`,
                    description: `Dernier contact avec ${companyData.name} le ${lastContact.toLocaleDateString('fr-FR')} (${Math.round(daysSinceContact)} jours). Opportunité de réactivation commerciale.`,
                    importance: daysSinceContact > 365 ? 'medium' : 'low',
                    reason: `Pas de contact depuis ${Math.round(daysSinceContact)} jours`,
                    actionSuggestion: 'Call de suivi, audit gratuit, présentation nouveaux services, invitation événement',
                    estimatedValue: '5000-30000',
                    urgency: 'low',
                    contactInfo
                });
            }
        }

        // 7. Priority/Status based signals
        if (companyData.priority === 'high' && companyData.status !== 'client') {
            signals.push({
                id: 'high-priority-prospect',
                type: 'other',
                title: `${companyData.name} - Prospect prioritaire`,
                description: `${companyData.name} est marquée comme priorité haute. Opportunité de conversion en client.`,
                importance: 'high',
                reason: 'Prospect haute priorité non converti',
                actionSuggestion: 'Offre personnalisée, proposition de valeur renforcée, négociation directe',
                estimatedValue: '20000-100000',
                urgency: 'high',
                contactInfo
            });
        }

        // 8. Next action overdue
        if (companyData.nextActionAt) {
            const nextActionDate = new Date(companyData.nextActionAt);
            const daysOverdue = (now.getTime() - nextActionDate.getTime()) / (1000 * 60 * 60 * 24);
            
            if (daysOverdue > 0) {
                signals.push({
                    id: 'overdue-action',
                    type: 'other',
                    title: `${companyData.name} - Action en retard`,
                    description: `Action prévue le ${nextActionDate.toLocaleDateString('fr-FR')} non réalisée (${Math.round(daysOverdue)} jours de retard).${companyData.nextAction ? ` Action: ${companyData.nextAction}` : ''}`,
                    importance: daysOverdue > 30 ? 'high' : 'medium',
                    reason: `Action en retard de ${Math.round(daysOverdue)} jours`,
                    actionSuggestion: 'Rattrapage immédiat, réplanification, justification retard',
                    estimatedValue: '1000-10000',
                    urgency: 'high',
                    contactInfo
                });
            }
        }

        setDetectedSignals(signals);
    };

    const updateSignalContent = (signalId: string, field: 'title' | 'description', value: string) => {
        setDetectedSignals(prev => prev.map(signal => 
            signal.id === signalId 
                ? { ...signal, [field]: value }
                : signal
        ));
    };

    const toggleEditMode = (signalId: string) => {
        setEditingSignal(editingSignal === signalId ? null : signalId);
    };

    const handleKeyDown = (e: React.KeyboardEvent, signalId: string) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            // Ctrl+Enter to save and exit edit mode
            e.preventDefault();
            setEditingSignal(null);
        } else if (e.key === 'Escape') {
            // Escape to cancel edit mode
            e.preventDefault();
            setEditingSignal(null);
        }
    };

    const createSignalFromDetection = async (detectedSignal: any) => {
        if (!company) return;
        
        setCreatingSignal(detectedSignal.id);
        
        // Exit edit mode if currently editing this signal
        if (editingSignal === detectedSignal.id) {
            setEditingSignal(null);
        }
        
        try {
            // Extract primary contact from founders or contacts
            let primaryContact = null;
            if (company.founders && company.founders.length > 0) {
                // Use the first founder as primary contact
                primaryContact = company.founders[0];
            } else if (company.contacts && company.contacts.length > 0) {
                // Use the primary contact or first contact
                primaryContact = company.contacts.find(c => c.isPrimary) || company.contacts[0];
            }

            // Extract all founders for investors array (when applicable)
            const founderNames = company.founders?.map(founder => {
                return founder.name || `${founder.firstName || ''} ${founder.lastName || ''}`.trim() || 'Unnamed Founder';
            }).filter(name => name !== 'Unnamed Founder') || [];

            // Determine amount and currency based on signal type and company data
            let amount: number | null = null;
            let currency = 'EUR';
            let valuation: number | null = null;
            
            // For funding-related signals, try to extract/estimate amounts
            if (['seed', 'series_a', 'series_b', 'series_c', 'series_d'].includes(detectedSignal.type)) {
                // Could be enhanced with actual funding data if available in company schema
                if (detectedSignal.type === 'seed') {
                    amount = 500000; // Default seed amount estimation
                } else if (detectedSignal.type === 'series_a') {
                    amount = 5000000; // Default Series A estimation
                } else if (detectedSignal.type === 'series_b') {
                    amount = 15000000; // Default Series B estimation
                }
            }

            // Build comprehensive signal data
            const signalData = {
                // Event information
                eventType: detectedSignal.type,
                title: detectedSignal.title,
                description: `${detectedSignal.description}\n\n🎯 Opportunité détectée automatiquement\n📊 Analyse: ${detectedSignal.reason}\n💡 Action suggérée: ${detectedSignal.actionSuggestion}`,
                
                // Company information
                company: company.name,
                companyWebsite: company.website || '',
                companyLogo: company.imageUrl || '',
                companyLocation: company.location || '',
                companyIndustry: company.industry || '',
                
                // Contact information (from primary contact)
                contactFirstName: primaryContact?.firstName || primaryContact?.name?.split(' ')[0] || '',
                contactLastName: primaryContact?.lastName || (primaryContact?.name?.split(' ').slice(1).join(' ')) || '',
                contactEmail: primaryContact?.email || '',
                contactLinkedin: primaryContact?.linkedinUrl || '',
                
                // Event details
                amount: amount,
                currency: currency,
                investors: founderNames.length > 0 ? founderNames : [],
                valuation: valuation,
                
                // Source and metadata
                source: 'Company Analysis - DuckFlow CRM',
                sourceUrl: company.website || '',
                imageUrl: company.imageUrl || '',
                publishedAt: publishImmediately ? new Date().toISOString() : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // Future date for drafts
                
                // Classification
                importance: detectedSignal.importance,
                tags: company.tags || {
                    franchise: "",
                    categories: [],
                    subCategories: [],
                }, // Use company tags structure
                
                // Additional metadata
                isActive: publishImmediately, // Only active if publishing immediately
                alreadyContacted: company.lastContactAt ? true : false,
                
                // Set expiration based on signal type (optional)
                expiresAt: (() => {
                    const now = new Date();
                    switch (detectedSignal.type) {
                        case 'seed':
                        case 'series_a':
                        case 'series_b':
                            // Funding signals expire in 6 months
                            return new Date(now.getTime() + (6 * 30 * 24 * 60 * 60 * 1000));
                        case 'expansion':
                            // Growth signals expire in 3 months
                            return new Date(now.getTime() + (3 * 30 * 24 * 60 * 60 * 1000));
                        case 'executive_change':
                            // Succession signals expire in 12 months
                            return new Date(now.getTime() + (12 * 30 * 24 * 60 * 60 * 1000));
                        default:
                            // Default 6 months expiration
                            return new Date(now.getTime() + (6 * 30 * 24 * 60 * 60 * 1000));
                    }
                })().toISOString()
            };

            // Remove empty/null values to clean up the payload, but preserve arrays with at least one element
            Object.keys(signalData).forEach(key => {
                const value = signalData[key as keyof typeof signalData];
                if (value === null || value === undefined || value === '') {
                    delete signalData[key as keyof typeof signalData];
                } else if (Array.isArray(value) && value.length === 0) {
                    // Only remove empty arrays for non-critical fields, keep tags even if empty
                    if (key !== 'tags') {
                        delete signalData[key as keyof typeof signalData];
                    }
                }
            });

            const newSignal = await createSignal(signalData);
            
            if (publishImmediately) {
                toast.success(`Signal "${newSignal.title}" créé et publié avec succès !`, {
                    position: "top-right",
                    autoClose: 3000,
                });
            } else {
                toast.success(`Signal "${newSignal.title}" créé en brouillon. Redirection vers la page d'édition...`, {
                    position: "top-right",
                    autoClose: 2000,
                });
                // Open signal edit page in new tab/window after a short delay
                setTimeout(() => {
                    window.open(`/bo/signals/edit/${newSignal._id}`, '_blank');
                }, 1000);
            }
            
            // Remove the detected signal from the list
            setDetectedSignals(prev => prev.filter(s => s.id !== detectedSignal.id));
            
        } catch (error: any) {
            console.error('Failed to create signal:', error);
            toast.error(
                error.response?.data?.message || 
                error.message || 
                'Erreur lors de la création du signal', 
                {
                    position: "top-right",
                    autoClose: 5000,
                }
            );
        } finally {
            setCreatingSignal(null);
        }
    };

    const fetchCompany = async (companyId: string) => {
        try {
            setLoading(true);
            const data = await getAdminCompany(companyId);
            setCompany(data);
        } catch (err) {
            console.error("Failed to fetch company:", err);
            setError("Failed to load company");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", { 
            month: "long", 
            day: "numeric", 
            year: "numeric" 
        });
    };

    const getStatusColor = (status?: CompanyStatus) => {
        switch (status) {
            case "prospect": return "bg-gray-100 text-gray-700 border-gray-200";
            case "contacted": return "bg-yellow-50 text-yellow-800 border-yellow-200";
            case "qualified": return "bg-purple-50 text-purple-800 border-purple-200";
            case "client": return "bg-green-50 text-green-800 border-green-200";
            case "inactive": return "bg-gray-50 text-gray-800 border-gray-200";
            default: return "bg-gray-50 text-gray-800 border-gray-200";
        }
    };

    const getPriorityColor = (priority?: string) => {
        switch (priority) {
            case "high": return "bg-red-50 text-red-800 border-red-200";
            case "medium": return "bg-yellow-50 text-yellow-800 border-yellow-200";
            case "low": return "bg-green-50 text-green-800 border-green-200";
            default: return "bg-gray-50 text-gray-800 border-gray-200";
        }
    };

    const getSizeLabel = (size?: CompanySize) => {
        const sizeOptions = {
            "1-10": "1-10 employees",
            "11-50": "11-50 employees",
            "51-200": "51-200 employees",
            "201-500": "201-500 employees",
            "500+": "500+ employees",
            "1000+": "1000+ employees",
        };
        return size ? sizeOptions[size] : "Unknown";
    };

    const getFundingStageLabel = (stage?: FundingStage) => {
        const stageOptions = {
            "bootstrap": "Bootstrap",
            "seed": "Seed",
            "series_a": "Series A",
            "series_b": "Series B",
            "series_c": "Series C",
            "series_d": "Series D",
            "ipo": "IPO",
            "acquired": "Acquired",
        };
        return stage ? stageOptions[stage] : "Unknown";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F0EB]">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">Loading company...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !company) {
        return (
            <div className="min-h-screen bg-[#F5F0EB]">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-600 text-sm">{error || "Company not found"}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-6xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate("/bo/companies")}
                                className="p-2 hover:bg-gray-100 rounded-xs transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-xl font-semibold text-black dark:text-white">
                                    Company Details
                                </h1>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    View and manage company information
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(`/bo/companies/edit/${company._id}`)}
                            className="flex text-xs items-center gap-2 bg-black text-white px-3 py-1.5 rounded-xs hover:bg-gray-800 transition-colors"
                        >
                            <Pencil className="w-4 h-4" />
                            Edit Company
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <BONavigation />

                {/* Company Header */}
                <div className="bg-white border border-gray-200 rounded-xs p-6 mb-6">
                    <div className="flex items-start gap-4">
                        {company.imageUrl ? (
                            <img
                                src={company.imageUrl}
                                alt={company.name}
                                className="w-16 h-16 object-cover rounded-xs border border-gray-200"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded-xs border border-gray-200 flex items-center justify-center">
                                <Building2 className="w-8 h-8 text-gray-400" />
                            </div>
                        )}
                        
                        <div className="flex-1">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h2 className="text-2xl font-semibold text-gray-900">{company.name}</h2>
                                    <p className="text-sm text-gray-600 font-mono mt-1">SIREN: {company.siren}</p>
                                    {company.description && (
                                        <p className="text-sm text-gray-700 mt-2 max-w-2xl">{company.description}</p>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 text-sm font-medium rounded-sm border ${getStatusColor(company.status)}`}>
                                        {company.status || "Unknown"}
                                    </span>
                                    {company.priority && (
                                        <span className={`px-3 py-1 text-sm font-medium rounded-sm border ${getPriorityColor(company.priority)}`}>
                                            {company.priority} priority
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-6 mt-4">
                                {company.website && (
                                    <a
                                        href={company.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-gray-900 hover:underline"
                                    >
                                        <Globe className="w-4 h-4" />
                                        Website
                                    </a>
                                )}
                                {company.location && (
                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                        <MapPin className="w-4 h-4" />
                                        {company.location}
                                    </div>
                                )}
                                {company.industry && (
                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                        <Building2 className="w-4 h-4" />
                                        {company.industry}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Company Details */}
                        <div className="bg-white border border-gray-200 rounded-xs p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {company.size && (
                                    <div className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Company Size</p>
                                            <p className="text-sm text-gray-600">{getSizeLabel(company.size)}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {company.fundingStage && (
                                    <div className="flex items-center gap-2">
                                        <Banknote className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Funding Stage</p>
                                            <p className="text-sm text-gray-600">{getFundingStageLabel(company.fundingStage)}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {company.lastContactAt && (
                                    <div className="flex items-center gap-2">
                                        <CalendarDays className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Last Contact</p>
                                            <p className="text-sm text-gray-600">{formatDate(company.lastContactAt)}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {company.nextActionAt && (
                                    <div className="flex items-center gap-2">
                                        <CalendarDays className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Next Action</p>
                                            <p className="text-sm text-gray-600">{formatDate(company.nextActionAt)}</p>
                                            {company.nextAction && (
                                                <p className="text-xs text-gray-500 mt-1">{company.nextAction}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {company.tags && (company.tags.franchise || (company.tags.categories && company.tags.categories.length > 0) || (company.tags.subCategories && company.tags.subCategories.length > 0)) && (
                                <div className="mt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Tag className="w-5 h-5 text-gray-400" />
                                        <p className="text-sm font-medium text-gray-900">Tags</p>
                                    </div>
                                    <div className="space-y-2">
                                        {company.tags.franchise && (
                                            <div>
                                                <span className="text-xs font-medium text-gray-500 mr-2">Franchise:</span>
                                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-sm border border-gray-200">
                                                    {company.tags.franchise}
                                                </span>
                                            </div>
                                        )}
                                        {company.tags.categories && company.tags.categories.length > 0 && (
                                            <div>
                                                <span className="text-xs font-medium text-gray-500 mr-2">Catégories:</span>
                                                <div className="inline-flex flex-wrap gap-1">
                                                    {company.tags.categories.map((category, index) => (
                                                        <span
                                                            key={index}
                                                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-sm border border-green-200"
                                                        >
                                                            {category}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {company.tags.subCategories && company.tags.subCategories.length > 0 && (
                                            <div>
                                                <span className="text-xs font-medium text-gray-500 mr-2">Sous-catégories:</span>
                                                <div className="inline-flex flex-wrap gap-1">
                                                    {company.tags.subCategories.map((subCategory, index) => (
                                                        <span
                                                            key={index}
                                                            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-sm border border-purple-200"
                                                        >
                                                            {subCategory}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {company.notes && (
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-900 mb-2">Notes</p>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{company.notes}</p>
                                </div>
                            )}
                        </div>

                        {/* Founders */}
                        {company.founders && company.founders.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xs p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <User className="w-5 h-5" />
                                    Founders ({company.founders.length})
                                </h3>
                                
                                <div className="space-y-4">
                                    {company.founders.map((founder, index) => (
                                        <div key={index} className="border border-gray-200 rounded-xs p-4">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900">
                                                        {founder.name || `${founder.firstName || ''} ${founder.lastName || ''}`.trim() || "Unnamed Founder"}
                                                    </h4>
                                                    {founder.title && (
                                                        <p className="text-sm text-gray-600 mt-1">{founder.title}</p>
                                                    )}
                                                </div>
                                                
                                                <div className="flex items-center gap-2">
                                                    {founder.email && (
                                                        <a
                                                            href={`mailto:${founder.email}`}
                                                            className="text-gray-900 hover:underline"
                                                            title={founder.email}
                                                        >
                                                            <Mail className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {founder.phone && (
                                                        <a
                                                            href={`tel:${founder.phone}`}
                                                            className="text-green-600 hover:text-green-800"
                                                            title={founder.phone}
                                                        >
                                                            <Phone className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {founder.linkedinUrl && (
                                                        <a
                                                            href={founder.linkedinUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-gray-900 hover:underline"
                                                            title="LinkedIn Profile"
                                                        >
                                                            <User className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Contacts */}
                        <div className="bg-white border border-gray-200 rounded-xs p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Contacts ({company.contacts?.length || 0})
                            </h3>
                            
                            {company.contacts && company.contacts.length > 0 ? (
                                <div className="space-y-3">
                                    {company.contacts.map((contact, index) => (
                                        <div key={index} className="border border-gray-200 rounded-xs p-3">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900">
                                                        {contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || "Unnamed Contact"}
                                                    </h4>
                                                    {contact.title && (
                                                        <p className="text-xs text-gray-600 mt-1">{contact.title}</p>
                                                    )}
                                                    {contact.isPrimary && (
                                                        <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-sm mt-1">
                                                            Primary
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                {contact.email && (
                                                    <a
                                                        href={`mailto:${contact.email}`}
                                                        className="text-gray-900 hover:underline"
                                                        title={contact.email}
                                                    >
                                                        <Mail className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {contact.phone && (
                                                    <a
                                                        href={`tel:${contact.phone}`}
                                                        className="text-green-600 hover:text-green-800"
                                                        title={contact.phone}
                                                    >
                                                        <Phone className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {contact.linkedinUrl && (
                                                    <a
                                                        href={contact.linkedinUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-gray-900 hover:underline"
                                                        title="LinkedIn Profile"
                                                    >
                                                        <User className="w-4 h-4" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No contacts added yet.</p>
                            )}
                        </div>

                        {/* Metadata */}
                        <div className="bg-white border border-gray-200 rounded-xs p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h3>
                            
                            <div className="space-y-3 text-sm">
                                <div>
                                    <p className="font-medium text-gray-900">Created</p>
                                    <p className="text-gray-600">{formatDate(company.createdAt)}</p>
                                </div>
                                
                                <div>
                                    <p className="font-medium text-gray-900">Last Updated</p>
                                    <p className="text-gray-600">{formatDate(company.updatedAt)}</p>
                                </div>
                                
                                <div>
                                    <p className="font-medium text-gray-900">Company ID</p>
                                    <p className="text-gray-600 font-mono text-xs">{company._id}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Market Signals Detection - Full Width Section */}
                {detectedSignals.length > 0 && (
                    <div className="mt-8">
                        <div className="mb-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
                                <Signal className="w-5 h-5 text-gray-600" />
                                Signaux Détectés ({detectedSignals.length})
                            </h3>
                            <p className="text-sm text-gray-600">
                                Opportunités commerciales détectées automatiquement. Cliquez pour modifier.
                            </p>
                        </div>
                        
                        {/* Signals List - Full width layout */}
                        <div className="space-y-4">
                            {detectedSignals.map((signal) => (
                                <div key={signal.id} className="bg-white border border-gray-200 rounded-sm p-6">
                                    {/* Header row */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                {signal.type}
                                            </span>
                                            <span className="text-xs text-gray-400">•</span>
                                            <span className="text-xs text-gray-500">
                                                {signal.importance}
                                            </span>
                                            {signal.urgency && (
                                                <>
                                                    <span className="text-xs text-gray-400">•</span>
                                                    <span className="text-xs text-gray-500">
                                                        {signal.urgency} urgency
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        
                                        <button
                                            onClick={() => toggleEditMode(signal.id)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                            title={editingSignal === signal.id ? "Arrêter l'édition" : "Modifier le signal"}
                                        >
                                            <PenSquare className="w-4 h-4" />
                                        </button>
                                    </div>
                                    
                                    {/* Title and Description */}
                                    <div className="mb-4">
                                        {editingSignal === signal.id ? (
                                            <input
                                                type="text"
                                                value={signal.title}
                                                onChange={(e) => updateSignalContent(signal.id, 'title', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, signal.id)}
                                                className="w-full text-lg font-medium text-gray-900 border border-gray-300 rounded-sm px-3 py-2 mb-3 focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                                                placeholder="Titre du signal..."
                                                autoFocus
                                            />
                                        ) : (
                                            <h4 
                                                className="text-lg font-medium text-gray-900 cursor-pointer hover:text-gray-700 transition-colors mb-2"
                                                onClick={() => setEditingSignal(signal.id)}
                                                title="Cliquer pour modifier"
                                            >
                                                {signal.title}
                                            </h4>
                                        )}
                                        
                                        {editingSignal === signal.id ? (
                                            <textarea
                                                value={signal.description}
                                                onChange={(e) => updateSignalContent(signal.id, 'description', e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(e, signal.id)}
                                                className="w-full text-sm text-gray-600 border border-gray-300 rounded-sm px-3 py-2 leading-relaxed resize-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                                                placeholder="Description du signal..."
                                                rows={3}
                                            />
                                        ) : (
                                            <p 
                                                className="text-sm text-gray-600 leading-relaxed cursor-pointer hover:text-gray-800 transition-colors"
                                                onClick={() => setEditingSignal(signal.id)}
                                                title="Cliquer pour modifier"
                                            >
                                                {signal.description}
                                            </p>
                                        )}
                                    </div>
                                    
                                    {/* Signal Details in a grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 text-xs text-gray-500">
                                        <div>
                                            <span className="font-medium">Détection:</span>
                                            <div className="text-gray-600 mt-1">{signal.reason}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium">Action suggérée:</span>
                                            <div className="text-gray-600 mt-1">{signal.actionSuggestion}</div>
                                        </div>
                                        {signal.estimatedValue && (
                                            <div>
                                                <span className="font-medium">Valeur estimée:</span>
                                                <div className="text-gray-600 mt-1">{signal.estimatedValue}€</div>
                                            </div>
                                        )}
                                        {signal.contactInfo && (
                                            <div>
                                                <span className="font-medium">Contact:</span>
                                                <div className="text-gray-600 mt-1">
                                                    {signal.contactInfo.name}
                                                    {signal.contactInfo.title && ` (${signal.contactInfo.title})`}
                                                    {signal.contactInfo.email && (
                                                        <div>
                                                            <a href={`mailto:${signal.contactInfo.email}`} className="text-gray-700 hover:underline">
                                                                {signal.contactInfo.email}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Actions row */}
                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <label className="flex items-center gap-2 text-xs text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={publishImmediately}
                                                    onChange={(e) => setPublishImmediately(e.target.checked)}
                                                    className="w-3 h-3 text-gray-600 border-gray-300 rounded focus:ring-gray-500 focus:ring-1"
                                                />
                                                Publier immédiatement
                                            </label>
                                            
                                            {editingSignal === signal.id && (
                                                <span className="text-xs text-gray-500">
                                                    Ctrl+Entrée pour valider, Échap pour annuler
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            {editingSignal === signal.id && (
                                                <button
                                                    onClick={() => setEditingSignal(null)}
                                                    className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded-sm hover:bg-gray-700 transition-colors"
                                                >
                                                    Valider
                                                </button>
                                            )}
                                            <button
                                                onClick={() => createSignalFromDetection(signal)}
                                                disabled={creatingSignal === signal.id}
                                                className="px-3 py-1.5 text-xs bg-black text-white rounded-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {creatingSignal === signal.id ? (
                                                    <>
                                                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin inline-block mr-1"></div>
                                                        Création...
                                                    </>
                                                ) : (
                                                    'Créer Signal'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}